import {
  DateRangeFilterConfig,
  FilterConfig,
  FilterGroupConfig,
  InputFilterConfig,
  SearchInputFilterConfig,
} from 'components/SearchAndFilters/FilterTypes';
import {
  MultiSelectFilterConfig,
  MultiSelectRenderStyle,
} from 'components/SearchAndFilters/MultiSelect';
import { useAtomValue, useSetAtom } from 'jotai';
import { verifyAccessToken } from 'lib/auth';
import axios from 'lib/axiosInstance';
import React, { useEffect, useState } from 'react';
import { companyIdAtom } from 'views/state';

import {
  filterValuesAtom,
  isQuerystringFiltersInitializedAtom,
  parsedFilterValuesAtom,
} from './state';

const ServerOrWorkstationFilter = [
  { value: '', label: 'All Types' },
  { value: 'Server', label: 'All Servers' },
  { value: 'Workstations', label: 'All Workstations' },
];

export const useFilterConfig = (): FilterConfig => {
  const setFilterValues = useSetAtom(filterValuesAtom);
  const filterValues = useAtomValue(parsedFilterValuesAtom);
  const setQuerystringFiltersInitialized = useSetAtom(
    isQuerystringFiltersInitializedAtom,
  );
  const companyId = useAtomValue(companyIdAtom);

  const [activationKeyOptions, setActivationKeyOptions] = useState<
    { value: string; label: string }[]
  >([]);

  useEffect(() => {
    // Only change if filter is empty or undefined
    if (!filterValues.filter) {
      setFilterValues((prevValues) => ({
        ...prevValues,
        filter: ['active'],
      }));
    }
  }, [setFilterValues]);

  useEffect(() => {
    const fetchActivationKeys = async () => {
      try {
        await verifyAccessToken();
        const url = '/cloud_agents/activation-keys/' + companyId + '/';
        const headers = {
          Authorization: 'Bearer ' + localStorage.getItem('access'),
        };

        const res = await axios.get(url, { headers });
        if (res.status === 201 || res.status === 200) {
          const data = res.data;
          const formattedOptions: {
            value: string;
            label: string;
          }[] = Object.entries(data)
            .filter(([key]) => key !== 'customerId')
            .map(([key, value]) => ({
              value: String(value),
              label: String(key).replace(/^./, (str) => str.toUpperCase()),
            }));
          setActivationKeyOptions(formattedOptions);
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error fetching activation keys:', error);
      }
    };

    if (companyId) {
      void fetchActivationKeys();
    }
  }, [companyId]);

  // Generate host type options
  const hostTypeOptions = React.useMemo(() => {
    const formattedAgentKeys = activationKeyOptions.map((option) => ({
      ...option,
      label: '\u00A0\u00A0 ' + option.label,
    }));

    const workstationOptions = formattedAgentKeys.filter((opt) =>
      opt.label.toLowerCase().includes('workstation'),
    );
    const serverOptions = formattedAgentKeys.filter(
      (opt) => !opt.label.toLowerCase().includes('workstation'),
    );

    return [
      ServerOrWorkstationFilter[0],
      ServerOrWorkstationFilter[1],
      ...serverOptions,
      ServerOrWorkstationFilter[2],
      ...workstationOptions,
    ];
  }, [activationKeyOptions]);

  // Initialize filter values from query string
  useEffect(() => {
    void setQuerystringFiltersInitialized(false);

    const queryFilters: Record<string, any> = {};
    const initQueryFilters = async () => {};

    void initQueryFilters()
      .then(() => {
        if (Object.keys(queryFilters).length > 0) {
          void setFilterValues(queryFilters);
        }
      })
      .finally(() => {
        void setQuerystringFiltersInitialized(true);
      });

    return () => {
      void setQuerystringFiltersInitialized(false);
    };
  }, []);

  const config = React.useMemo(() => {
    const newConfig = new FilterConfig({
      searchFilterId: 'search',
      rightPanelFilters: ['filter', 'os'],
      leftPanelFilters: ['dateRangeFilters', 'address'],
      filters: [
        new SearchInputFilterConfig({
          id: 'search',
          label: 'Search',
          placeholder: 'Search and Filter',
          maxLength: 250,
          debounceTime: 1000,
          enableDebounce: true,
          inputProps: { type: 'text' },
          showInFilterMenu: false,
        }),
        new MultiSelectFilterConfig({
          id: 'os',
          label: 'Operating System',
          placeholder: 'Select OS',
          tooltip: (
            <div>
              <p>Filter hosts by Operating Systems (multi select).</p>
            </div>
          ),
          iconClass: 'fas fa-desktop',
          defaultSelectedValues: [''],
          options: [
            { label: 'All', value: '', deselectOthers: true },
            { label: 'Windows', value: 'windows' },
            { label: 'Linux', value: 'linux' },
            { label: 'Unix', value: 'unix' },
            { label: 'MacOS', value: 'macos' },
            { label: 'Other', value: 'other', deselectOthers: true },
          ],
          renderStyle: MultiSelectRenderStyle.Badges,
        }),
        new InputFilterConfig({
          id: 'address',
          label: 'IP Address',
          tooltip: 'Filter by Host IP Address.',
          iconClass: 'fas fa-id-card',
          inputProps: {
            type: 'text',
            placeholder: 'Host IP Address',
            maxLength: 50,
          },
          debounceTime: 1000,
          enableDebounce: true,
        }),
        new MultiSelectFilterConfig({
          id: 'filter',
          label: 'Status',
          tooltip: 'Filter by cloud agent status.',
          iconClass: 'fas fa-clipboard-check',
          defaultSelectedValues: [''],
          options: [
            {
              value: '',
              label: 'All',
              deselectOthers: true,
            },
            {
              value: 'active',
              label: 'Active',
              deselectOthers: true,
            },
            {
              value: 'aging',
              label: 'Only In Aging Out Process',
              deselectOthers: true,
            },
            {
              value: 'deleted',
              label: 'Deleted (Aged Out or Manual)',
              deselectOthers: true,
            },
          ],
          renderStyle: MultiSelectRenderStyle.Badges,
        }),
        new FilterGroupConfig({
          id: 'dateRangeFilters',
          label: 'Filter by Date',
          iconClass: 'fas fa-calendar-alt',
          tooltip:
            'Filter by date and timestamps. The From and To dates are inclusive.',
          filters: [
            new DateRangeFilterConfig({
              id: 'ca_created',
              label: 'Created Date',
              labelWidth: '115px',
            }),
            new DateRangeFilterConfig({
              id: 'ca_modified',
              label: 'Modified Date',
              labelWidth: '115px',
            }),
            new DateRangeFilterConfig({
              id: 'last_vuln_scan',
              label: 'Last Scan Date',
              labelWidth: '115px',
            }),
            new DateRangeFilterConfig({
              id: 'last_checked_in',
              label: 'Checked In Date',
              labelWidth: '115px',
            }),
          ],
        }),
      ],
    });

    // Apply initial filter values
    newConfig.applyInitialFilterValues(filterValues);

    return newConfig;
  }, [filterValues, hostTypeOptions]);

  return config;
};
